import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Banknote, Hash, Hotel, Scale, Ticket, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatPriceMinor } from '@/routes/disruptions/labels';
import { factsByKind } from '@/lib/why/format';
import type { NarrativeFact, NarrativeFactKind } from '@/lib/why/types';

/**
 * The evidence panel, dispatched on FACT KIND — never on chapter kind.
 *
 * This is load-bearing, not a style choice. The journal's chapters carry a
 * `kind` too, but rendering by it would hard-code today's five chapters into
 * the UI; keying off `INarrativeFact.kind` means slice W8's richer `HOTEL`
 * facts (the constraint that killed each hotel, per group) land here with ZERO
 * change. `data` is open by contract — the renderer walks whatever it is given
 * rather than a fixed field list, so a fact the UI has never seen still shows
 * its evidence instead of disappearing.
 *
 * NOTHING IS INVENTED HERE. Values are rendered verbatim from `data`; a field
 * with no translated label falls back to its own wire key, which is the honest
 * answer rather than a guessed caption. A nested object is printed as-is, in a
 * scroll container, because that IS the evidence (a policy snapshot's knobs).
 */

const KIND_ICON: Record<NarrativeFactKind, typeof Scale> = {
  POLICY: Scale,
  REASON: TriangleAlert,
  COUNT: Hash,
  PNR: Ticket,
  MONEY: Banknote,
  HOTEL: Hotel,
};

/** Wire keys that carry the policy document a `POLICY` fact is about. */
const POLICY_URN_KEYS = ['policyUrn', 'governingPolicyUrn', 'urn'] as const;
/** Wire keys whose value is a minor-unit amount. */
const MINOR_SUFFIX = /Minor$/;
/** Wire keys carrying the ISO-4217 code that the minor amounts are in. */
const CURRENCY_KEYS = ['currency', 'currencyCode'] as const;

function firstString(data: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const k of keys) {
    const v = data[k];
    if (typeof v === 'string' && v) return v;
  }
  return undefined;
}

function isPrimitive(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/**
 * Every fact of a chapter, grouped by kind.
 *
 * `citedIds` are the ids the account's prose cites (`narration.factRefs`). The
 * marker is the whole point of putting the evidence under the account: an
 * operator can check each claim against the fact it was written from, without
 * leaving the dialog.
 */
export function FactList({
  facts,
  citedIds,
  testIdPrefix,
}: {
  facts: readonly NarrativeFact[];
  citedIds?: ReadonlySet<string>;
  testIdPrefix: string;
}) {
  const { t } = useTranslation('why');
  const groups = factsByKind(facts);

  if (!groups.length) {
    return (
      <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-facts-none`}>
        {t('facts.none')}
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-facts`}>
      {groups.map(([kind, group]) => {
        const Icon = KIND_ICON[kind];
        return (
          <section key={kind} data-testid={`${testIdPrefix}-facts-${kind}`}>
            <h5 className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <Icon className="h-3 w-3 shrink-0" aria-hidden />
              {t(`facts.kind.${kind}`)}
            </h5>
            <ul className="space-y-1.5">
              {group.map((fact) => (
                <FactRow
                  key={fact.id}
                  fact={fact}
                  cited={!!citedIds?.has(fact.id)}
                  testIdPrefix={testIdPrefix}
                />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function FactRow({
  fact,
  cited,
  testIdPrefix,
}: {
  fact: NarrativeFact;
  cited: boolean;
  testIdPrefix: string;
}) {
  const { t } = useTranslation('why');
  const data = fact.data && typeof fact.data === 'object' ? fact.data : {};
  const policyUrn = fact.kind === 'POLICY' ? firstString(data, POLICY_URN_KEYS) : undefined;
  const currency = firstString(data, CURRENCY_KEYS);
  const entries = Object.entries(data).filter(([k]) => k !== 'currency');

  return (
    <li
      className={cn(
        'rounded-md border border-border bg-muted/30 px-2.5 py-2',
        cited && 'border-primary/40 bg-primary/5',
      )}
      data-testid={`${testIdPrefix}-fact`}
      data-fact-id={fact.id}
      data-fact-kind={fact.kind}
      data-cited={cited ? 'true' : 'false'}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <code className="min-w-0 break-all font-mono text-[10.5px] text-muted-foreground">
          {fact.id}
        </code>
        {cited ? (
          <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-primary">
            {t('facts.cited')}
          </span>
        ) : null}
      </div>

      {entries.length ? (
        <dl className="mt-1 grid gap-x-3 gap-y-1 sm:grid-cols-[minmax(6rem,auto)_minmax(0,1fr)]">
          {entries.map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-[11px] text-muted-foreground">
                {t(`facts.field.${key}`, { defaultValue: key })}
              </dt>
              <dd className="min-w-0 text-[11.5px] font-medium text-foreground">
                <FactValue fieldKey={key} value={value} currency={currency} />
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {policyUrn ? (
        <Link
          to={`/policies/${encodeURIComponent(policyUrn)}`}
          className="mt-1.5 inline-flex text-[11px] font-semibold text-primary hover:underline"
          data-testid={`${testIdPrefix}-fact-policy-link`}
        >
          {t('facts.openPolicy')}
        </Link>
      ) : null}
    </li>
  );
}

/**
 * One field's value, verbatim. Minor-unit amounts are formatted with the fact's
 * own currency (never a guessed one — no currency ⇒ the raw integer stands, so
 * nobody reads cents as dollars). Structured values print as JSON in their own
 * scroll container (§18.2: wide content scrolls itself, the page never does).
 */
function FactValue({
  fieldKey,
  value,
  currency,
}: {
  fieldKey: string;
  value: unknown;
  currency: string | undefined;
}) {
  const { t } = useTranslation('why');

  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">{t('facts.value.absent')}</span>;
  }
  if (typeof value === 'boolean') {
    return <span>{value ? t('facts.value.yes') : t('facts.value.no')}</span>;
  }
  if (typeof value === 'number' && MINOR_SUFFIX.test(fieldKey) && currency) {
    // Money on the airline wire is the PRICE (`airline-money@v2`) — never Nexa's cost.
    return <span className="tabular-nums">{formatPriceMinor(value, currency, t)}</span>;
  }
  if (typeof value === 'number') {
    return <span className="tabular-nums">{value.toLocaleString()}</span>;
  }
  if (typeof value === 'string') {
    return <span className="break-words">{value}</span>;
  }
  if (Array.isArray(value) && value.every(isPrimitive)) {
    return <span className="break-words">{value.map(String).join(' · ')}</span>;
  }
  return (
    <pre className="max-h-40 overflow-auto rounded bg-background/70 p-1.5 font-mono text-[10.5px] leading-snug">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}
